// A deal's documents (TX-US-0031): contracts, KYC packs, invoices, whatever its papers are.
// Everyone who reads the deal opens them and drops new ones on the zone; a dropped file uploads at
// once, because the deal it belongs to already exists. A document is removed only by the person
// who uploaded it (ADR-0012), so only they are offered the control.
//
// With the deal flow on (TX-US-0043), the files dropped are given a type first: a drop is refused
// until one is chosen, and the files then upload at once. The list is grouped by the stage each
// document was added at, with its type - which its uploader changes when it was wrong.

import { useCallback, useEffect, useState } from 'react';

import { deleteDocument, getDocumentDownload, listDocuments, retypeDocument } from '../api.js';
import { documentGroups, flowOn } from '../deals/flow.js';
import { STATES, formatBytes } from '../deliveries/attachments/uploads.js';
import useAttachmentUploads from '../deliveries/attachments/useAttachmentUploads.js';
import { useMe } from '../MeContext.js';
import DropZone from '../ui/DropZone.jsx';
import Icon from '../ui/Icon.jsx';
import { ACCEPT, FORMATS_HINT } from './dealFiles.js';
import { DEAL_DOCUMENTS_API } from './dealUploadApi.js';
import { DOCUMENT_TYPES, rememberType, typeLabel } from './documentTypes.js';
import DocumentTypeSelect from './DocumentTypeSelect.jsx';

const NO_TYPE = 'Choose the type of the files you drop first.';

const STATE_LABEL = {
  [STATES.queued]: 'Waiting',
  [STATES.uploading]: 'Uploading…',
  [STATES.done]: 'Uploaded',
  [STATES.failed]: 'Failed',
};

// A signed URL is cross-origin, so `download` on a link is ignored and the browser navigates.
function open(url) {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  link.click();
}

// `onChanged` is told after a document arrives, leaves or changes type: what the deal's next move
// waits on may be a document.
export default function DealDocuments({ preDealId, onChanged }) {
  const me = useMe();
  const flow = flowOn(me);
  const [documents, setDocuments] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(null);
  // The type the next files dropped are given; none until it is chosen.
  const [dropType, setDropType] = useState('');
  const { items, pending, draining, add, drop, drain } = useAttachmentUploads(DEAL_DOCUMENTS_API);

  const load = useCallback(
    () => listDocuments(preDealId)
      // A PENDING record is an upload that never reached storage; it is not a document yet.
      .then((found) => setDocuments(found.filter((doc) => doc.status === 'AVAILABLE')))
      .catch((err) => setError(err.message)),
    [preDealId],
  );

  const changed = useCallback(() => load().then(() => onChanged?.()), [load, onChanged]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (pending === 0 || draining) return;
    drain(preDealId).then(changed);
  }, [preDealId, pending, draining, drain, changed]);

  const addTyped = (files) => {
    if (!dropType) {
      setError(NO_TYPE);
      return;
    }
    setError(null);
    for (const file of files) rememberType(file, dropType);
    add(files);
  };

  const download = (doc) => getDocumentDownload(preDealId, doc.document_id)
    .then((found) => open(found.download_url))
    .catch((err) => setError(err.message));

  const retype = async (doc, documentType) => {
    try {
      await retypeDocument(preDealId, doc.document_id, documentType);
      await changed();
    } catch (err) {
      setError(err.message);
    }
  };

  // Two presses: the first arms the button, the second deletes.
  const remove = async (doc) => {
    if (confirming !== doc.document_id) {
      setConfirming(doc.document_id);
      return;
    }
    setConfirming(null);
    try {
      await deleteDocument(preDealId, doc.document_id);
      await changed();
    } catch (err) {
      setError(err.message);
    }
  };

  const empty = documents && documents.length === 0 && items.length === 0;

  const row = (doc) => (
    <DocumentRow key={doc.document_id} doc={doc} mine={doc.uploaded_by === me?.email} flow={flow}
                 arming={confirming === doc.document_id} onDownload={download} onRemove={remove}
                 onDisarm={() => setConfirming(null)} onRetype={retype} />
  );

  return (
    <section className="attachments" aria-labelledby={`documents-${preDealId}`}>
      <h3 id={`documents-${preDealId}`}>Documents</h3>

      {error && <p className="error">{error}</p>}

      {flow && (
        <label className="field drop-type">
          <span className="field-label">Type of the files you drop</span>
          <select value={dropType} onChange={(event) => setDropType(event.target.value)}
                  disabled={draining}>
            <option value="">Choose a type…</option>
            {DOCUMENT_TYPES.map((type) => (
              <option key={type.code} value={type.code}>{type.label}</option>
            ))}
          </select>
        </label>
      )}

      <DropZone accept={ACCEPT} onFiles={flow ? addTyped : add} disabled={draining}
                hint={FORMATS_HINT}>
        Drop files here, or choose them
      </DropZone>

      {empty && <p className="empty">No documents attached.</p>}

      {!empty && flow && documentGroups(documents ?? []).map((group) => (
        <div key={group.stage ?? 'none'} className="document-group">
          <h4>{group.label}</h4>
          <ul className="attachment-list" aria-label={`Documents added at ${group.label}`}>
            {group.documents.map(row)}
          </ul>
        </div>
      ))}

      {/* Without the flow, the documents and the files on their way share one list; with it, the
          documents are in their stage's group above and this list holds the files on their way. */}
      {!empty && (!flow || items.length > 0) && (
        <ul className="attachment-list" aria-label={flow ? 'Files on their way' : 'Documents'}>
          {!flow && (documents ?? []).map(row)}
          {items.map((item) => (
            <li key={item.key} className={`pending ${item.state}`}>
              <span className="attachment-name">
                <strong>{item.file.name}</strong>
                <small>
                  {formatBytes(item.file.size)} · {STATE_LABEL[item.state]}
                  {item.error && <span className="error"> — {item.error}</span>}
                </small>
              </span>
              {item.state !== STATES.uploading && (
                <button type="button" className="icon-button quiet" onClick={() => drop(item.key)}
                        aria-label={`Remove ${item.file.name}`} title="Remove">
                  <Icon name="close" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// One document: its name, what it is when the flow types documents, and its actions. Its uploader
// deletes it and, with the flow on, changes its type.
function DocumentRow({ doc, mine, flow, arming, onDownload, onRemove, onDisarm, onRetype }) {
  return (
    <li>
      <span className="attachment-name">
        <strong>{doc.filename}</strong>
        <small>
          {flow && !mine && <>{typeLabel(doc.document_type)} · </>}
          {formatBytes(doc.size_bytes ?? 0)} · {doc.uploaded_by}
          {' · '}{new Date(doc.confirmed_at ?? doc.created_at).toLocaleDateString()}
        </small>
      </span>
      <span className="row-actions">
        {flow && mine && (
          <DocumentTypeSelect label={`Type of ${doc.filename}`} value={doc.document_type}
                              onChange={(type) => onRetype(doc, type)} />
        )}
        <button type="button" className="icon-button quiet" onClick={() => onDownload(doc)}
                aria-label={`Download ${doc.filename}`} title="Download">
          <Icon name="download" />
        </button>
        {mine && (
          <button type="button"
                  className={`icon-button quiet${arming ? ' confirming' : ''}`}
                  onClick={() => onRemove(doc)}
                  onBlur={onDisarm}
                  aria-label={arming ? `Confirm delete ${doc.filename}` : `Delete ${doc.filename}`}
                  title={arming ? 'Click again to delete' : 'Delete'}>
            <Icon name="trash" />
          </button>
        )}
      </span>
    </li>
  );
}
